import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { getRequest, postRequest } from '../../../utils/api';
import { showToast } from '../../../utils/toast';

interface AssignPlanModalProps {
  show: boolean;
  onHide: () => void;
  user: any;
  onSuccess: () => void;
}

interface DiscountDetails {
  isCustomDiscountChecked: boolean;
  discountType: 'amount' | 'percentage';
  discountValue: number | '';
  discountReason: string;
}

const AssignPlanModal = ({ show, onHide, user, onSuccess }: AssignPlanModalProps) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [term, setTerm] = useState("monthly"); // 'monthly', 'yearly', 'lifetime', 'free'
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState("");

  // Custom Discount States
  const [customDiscount, setCustomDiscount] = useState<number>(0);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountDetails, setDiscountDetails] = useState<DiscountDetails>({
    isCustomDiscountChecked: false,
    discountType: 'amount',
    discountValue: '',
    discountReason: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [taxType, setTaxType] = useState('IGST');

  // --- DYNAMIC MATH CALCULATIONS ---
  const selectedPlanDetails = plans.find(p => p._id === selectedPlan);
  const planType = selectedPlanDetails?.ppm_plan_type || 'recurring';
  const mPrice = Number(selectedPlanDetails?.ppm_plan_monthly_price) || 0;
  const yPrice = Number(selectedPlanDetails?.ppm_plan_yearly_price) || 0;

  let grossAmount = 0;
  let termDiscount = 0;

  if (planType === 'free') {
    grossAmount = 0;
  } else if (planType === 'lifetime') {
    grossAmount = mPrice;
  } else {
    // Recurring
    if (term === 'monthly') {
      grossAmount = mPrice;
    } else if (term === 'yearly') {
      grossAmount = mPrice * 12;
      termDiscount = Math.max(0, grossAmount - yPrice);
    }
  }

  // Fetch plans & reset defaults when opened
  useEffect(() => {
    if (show && user) {
      fetchPlans();
      setStartDate(moment().format('YYYY-MM-DD'));
      setTerm("monthly");
      setCustomDiscount(0);
      setDiscountDetails({
        isCustomDiscountChecked: false,
        discountType: 'amount',
        discountValue: '',
        discountReason: ''
      });
    } else {
      setSelectedPlan("");
    }
  }, [show, user]);

  // Handle plan type switching
  useEffect(() => {
    if (planType === 'free') {
      setTerm('free');
    } else if (planType === 'lifetime') {
      setTerm('lifetime');
    } else if (planType === 'recurring' && (term === 'free' || term === 'lifetime')) {
      setTerm('monthly');
    }
  }, [selectedPlan, planType]);

  // Auto-calculate End Date
  useEffect(() => {
    if (startDate) {
      if (term === 'yearly') {
        setEndDate(moment(startDate).add(1, 'years').format('YYYY-MM-DD'));
      } else if (term === 'monthly') {
        setEndDate(moment(startDate).add(1, 'months').format('YYYY-MM-DD'));
      } else if (term === 'lifetime' || term === 'free') {
        setEndDate(moment(startDate).add(100, 'years').format('YYYY-MM-DD'));
      }
    } else {
      setEndDate("");
    }
  }, [startDate, term]);

  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const res = await getRequest(`/user/planPricingDropdownNew?user_id=${user._id}`);
      if (res.status === 'success') {
        setPlans(res.data.plans);
        setTaxType(res.data.taxType);
      }
    } catch (error) {
      console.error("Failed to fetch plans", error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleOpenDiscountModal = (e: React.MouseEvent) => {
    e.preventDefault();
    if (customDiscount > 0 && !discountDetails.isCustomDiscountChecked) {
      setDiscountDetails({
        ...discountDetails,
        isCustomDiscountChecked: true,
        discountType: 'amount',
        discountValue: customDiscount
      });
    }
    setShowDiscountModal(true);
  };

  const applyCustomDiscount = () => {
    let calculatedDiscount = 0;
    if (discountDetails.isCustomDiscountChecked && discountDetails.discountValue !== '') {
      const value = Number(discountDetails.discountValue);
      if (discountDetails.discountType === 'amount') {
        calculatedDiscount = value;
      } else if (discountDetails.discountType === 'percentage') {
        calculatedDiscount = (grossAmount * value) / 100;
      }
    }
    const maxDiscount = Math.max(0, grossAmount - termDiscount);
    setCustomDiscount(Math.min(calculatedDiscount, maxDiscount));
    setShowDiscountModal(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        userId: user._id,
        planData: {
          plan_id: selectedPlan,
          billing_cycle: term,
          start_date: startDate,
          end_date: endDate,
          custom_discount: customDiscount,
          discount_reason: discountDetails.discountReason,
          total_amount: finalTotal,
          taxable_amount: taxableAmount
        }
      }; 
      const response: any = await postRequest('/user/assign-new-user-plan', payload);

      if (response.status === 'success') {
        showToast.success(response.message || "Plan assigned successfully!");
        onSuccess();
        onHide();
      } else {
        showToast.error(response.message || "Failed to assign plan.");
      }
    } catch (error: any) {
      console.error("Error assigning plan:", error);
      showToast.error(error.message || "An error occurred while assigning the plan.");
    } finally {
      setIsSaving(false);
    }
  };

  // Math
  const taxableAmount = Math.max(0, grossAmount - termDiscount - customDiscount);
  const taxAmount = taxableAmount * 0.18;
  const finalTotal = taxableAmount + taxAmount;

  return (
    <>
      {/* --- MAIN MODAL --- */}
      <Modal className="m_modal" show={show} onHide={onHide} backdrop="static" size="lg">
        <div className="m_modal_head">
          <div className="titles">
            <div className="icon-badge bg-purple-light-5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
            </div>
            <div>
              <h2>Assign Plan</h2>
              <p>Configure billing cycle and discounts for this user.</p>
            </div>
          </div>
          <button className="close-btn" aria-label="Close" onClick={onHide}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"></path></svg>
          </button>
        </div>

        <div className="modal-body">
          {user ? (
            <div className="row g-3">
              <div className="col-lg-12">
                <div className="form-group mb-0">
                  <label>User</label>
                  <input 
                    type="text" 
                    className="form-control bg-light fw-bold" 
                    value={`${user.user_first_name} ${user.user_last_name} (${user.user_email})`} 
                    disabled 
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="form-group mb-0">
                  <label>Select Plan <span className="req">*</span></label>
                  <select
                    className="form-select"
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    disabled={loadingPlans || isSaving}
                  >
                    <option value="">{loadingPlans ? "Loading plans..." : "Select a plan..."}</option>
                    {plans.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {plan.ppm_plan_title} 
                        {plan.ppm_plan_type === 'free' ? ' (Free)' : 
                         plan.ppm_plan_type === 'lifetime' ? ` (₹${plan.ppm_plan_monthly_price} One-time)` : 
                         ` (₹${plan.ppm_plan_monthly_price}/mo)`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="form-group mb-0">
                  <label>Billing Cycle <span className="req">*</span></label>
                  <select 
                    className="form-select"
                    value={term} 
                    onChange={(e) => setTerm(e.target.value)}
                    disabled={planType === 'free' || planType === 'lifetime'}
                  >
                    {planType === 'free' && <option value="free">Free Forever</option>}
                    {planType === 'lifetime' && <option value="lifetime">One-time (Lifetime)</option>}
                    {planType === 'recurring' && (
                      <>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="form-group mb-0">
                  <label>Start Date <span className="req">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="form-group mb-0">
                  <label>End Date</label>
                  <input
                    type={term === 'lifetime' || term === 'free' ? 'text' : 'date'}
                    className="form-control bg-light"
                    value={term === 'lifetime' || term === 'free' ? 'Lifetime (No Expiry)' : endDate}
                    disabled
                  />
                </div>
              </div>

              {selectedPlan && planType !== 'free' && (
                <div className="col-lg-12 mt-4">
                  <div className="card card-border mb-0">
                    <div className="card-header border-bottom px-3 py-2 bg-light">
                      <h6 className="mb-0 fs-13">Payment Summary</h6>
                    </div>
                    <div className="card-body p-3">
                      <ul className="payment-summary list-unstyled mb-0">
                        <li className="d-flex justify-content-between mb-2">
                          <span>Base Amount ({term === 'lifetime' ? 'One-time' : term})</span>
                          <span className="fw-semibold text-dark">₹{grossAmount.toFixed(2)}</span>
                        </li>

                        {termDiscount > 0 && (
                          <li className="d-flex justify-content-between mb-2">
                            <span>Plan Discount</span>
                            <span className="text-success fw-semibold">- ₹{termDiscount.toFixed(2)}</span>
                          </li>
                        )}

                        <li className="d-flex justify-content-between mb-3">
                          <span>
                            <a href="#" onClick={isSaving ? undefined : handleOpenDiscountModal} className="text-primary text-decoration-none fw-semibold">
                              {customDiscount && customDiscount > 0 ? 'Edit Custom Discount' : '+ Add Custom Discount'}
                            </a>
                          </span>
                          <span className="text-success fw-semibold">- ₹{customDiscount.toFixed(2)}</span>
                        </li>

                        <li className="d-flex justify-content-between mb-2 border-top pt-3">
                          <span className="text-muted">Taxable Amount</span>
                          <span className="text-dark fw-semibold">₹{taxableAmount.toFixed(2)}</span>
                        </li>

                        {taxType === 'CGST_SGST' ? (
                          <>
                            <li className="d-flex justify-content-between mb-1 text-muted">
                              <span>CGST (9%)</span>
                              <span>₹{(taxAmount / 2).toFixed(2)}</span>
                            </li>
                            <li className="d-flex justify-content-between mb-3 text-muted">
                              <span>SGST (9%)</span>
                              <span>₹{(taxAmount / 2).toFixed(2)}</span>
                            </li>
                          </>
                        ) : (
                          <li className="d-flex justify-content-between mb-3 text-muted">
                            <span>IGST (18%)</span>
                            <span>₹{taxAmount.toFixed(2)}</span>
                          </li>
                        )}

                        <li className="d-flex justify-content-between border-top pt-3 fs-5">
                          <span className="fw-bold text-dark">Total</span>
                          <span className="fw-bold text-dark">₹{finalTotal.toFixed(2)}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p>Loading user data...</p>
          )}
        </div>
        
        <div className="modal-footer">
          <span className="footer-note">Fields marked * are required</span>
          <div className="footer-actions">
            <button className="btn btn-ghost" onClick={onHide} disabled={isSaving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={!selectedPlan || !startDate || isSaving}>
              {isSaving ? 'Assigning...' : 'Assign Plan'}
            </button>
          </div>
        </div>
      </Modal>

      {/* --- DISCOUNT MODAL --- */}
      <Modal className="m_modal" show={showDiscountModal} onHide={() => setShowDiscountModal(false)} backdrop="static" centered>
        <div className="m_modal_head">
          <div className="titles">
            <div className="icon-badge bg-green-light-5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
            </div>
            <div>
              <h2>{customDiscount && customDiscount > 0 ? 'Edit Discount' : 'Add Discount'}</h2>
              <p>Apply a custom order discount for this transaction.</p>
            </div>
          </div>
          <button className="close-btn" aria-label="Close" onClick={() => setShowDiscountModal(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"></path></svg>
          </button>
        </div>
        
        <div className="modal-body">
          <div className="row g-3">
            <div className="col-lg-12">
              <div className="form-group mb-0">
                <label>Discount codes</label>
                <input type="text" className="form-control bg-light" placeholder="Enter a discount code" disabled />
              </div>
            </div>

            <div className="col-lg-12 mt-4 mb-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="customDiscountCheck"
                  checked={discountDetails.isCustomDiscountChecked}
                  onChange={(e) => setDiscountDetails({ ...discountDetails, isCustomDiscountChecked: e.target.checked })}
                />
                <label className="form-check-label fw-semibold text-dark" htmlFor="customDiscountCheck">
                  Add custom order discount
                </label>
              </div>
            </div>

            {discountDetails.isCustomDiscountChecked && (
              <>
                <div className="col-lg-6">
                  <div className="form-group mb-0">
                    <label>Discount type</label>
                    <select
                      className="form-select"
                      value={discountDetails.discountType}
                      onChange={(e) => setDiscountDetails({ ...discountDetails, discountType: e.target.value as 'amount' | 'percentage' })}
                    >
                      <option value="amount">Amount</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>
                </div>
                
                <div className="col-lg-6">
                  <div className="form-group mb-0">
                    <label>Value</label>
                    <div className="input-group">
                      {discountDetails.discountType === 'amount' && <span className="input-group-text bg-light text-muted border-end-0">₹</span>}
                      <input
                        type="number"
                        className={`form-control ${discountDetails.discountType === 'amount' ? 'border-start-0 ps-0' : 'border-end-0 pe-0'}`}
                        min="0"
                        placeholder={discountDetails.discountType === 'amount' ? "0.00" : "0"}
                        value={discountDetails.discountValue}
                        onChange={(e) => setDiscountDetails({ ...discountDetails, discountValue: e.target.value === '' ? '' : Number(e.target.value) })}
                      />
                      {discountDetails.discountType === 'percentage' && <span className="input-group-text bg-light text-muted border-start-0">%</span>}
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-12 mt-4">
                  <div className="form-group mb-0">
                    <label>Reason for discount</label>
                    <input
                      type="text"
                      className="form-control"
                      value={discountDetails.discountReason}
                      onChange={(e) => setDiscountDetails({ ...discountDetails, discountReason: e.target.value })}
                    />
                    <div className="text-muted fs-8 mt-1">Visible to customer on their invoice</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <span className="footer-note"></span>
          <div className="footer-actions">
            <button className="btn btn-ghost" onClick={() => setShowDiscountModal(false)}>Cancel</button>
            <button 
              className="btn btn-primary" 
              onClick={applyCustomDiscount} 
              disabled={discountDetails.isCustomDiscountChecked && discountDetails.discountValue === ''}
            >
              Apply Discount
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AssignPlanModal;